/**
 * @prettier
 */
import {
    logExperiment as abacusLogExperiment,
    evaluateExperiment as abacusEvaluateExperiment
} from '@homeaway/abacus-client-utils';
import {evaluationIsVariant} from '@homeaway/react-experiment';

/*
    Abacus Test Helpers Notes.
    These functions are for the simple abacus logic that can either be used directly, or through
    generalized components.

    The Abacus Test Utils have a requirement of using the window.__ABACUS__ data
    by default, and require instanciating a new instance for other cases. They then fall back to odd
    values, and that should be addressed, perhaps.

    So, while hammering out these Abacus patterns, I chose to make these utils instead
    of having continual PRs against that other repo.

    These functions follow a pattern.  There is a 'normal' version of the function
    that uses the window.__ABACUS__ data, and then another version, usually called
    ---InData, that uses a given abacus test matrix for the first parameter.

    The reason is that server side rendering will not have the data in the global
    window, and a generalized window agnostic version of the function exists to hold
    any key logic.  However, it is more appropriately named.
 */

/**
 * Wrap the abacus test evaluator, and extended logic, that is common boilerplate.
 * Currently this assumes we know the variant if we want to log.
 * @param {object} bucketData - The data matrix returned from Abacus
 * @param {string} bucketName - The test name to evaluate.
 * @param {number|array} [variant=-1] - The target variant.  -1 (defaults) evaluates for all variants.
 * @param {boolean} [logEvaluation=false] - To execute the logging of a successful test.
 * @return {boolean}
 */
/* eslint-disable complexity */
function isInBucketData(bucketData, bucketName, variant = -1, logEvaluation = false) {
    // eslint-disable-line
    // evaluateExperiment just returns the bucket data from the global __ABACUS__
    const testData = bucketData[bucketName];

    if (!testData) {
        // If we are asking explicitly for the control variant, and there isn't any
        // test data for various reasons, consider it in the bucket.
        if (variant instanceof Array) {
            return variant.indexOf(0) !== -1;
        } else if (variant === 0) {
            return true;
        }

        // This means the test is either not live, or something is wrong.
        return false;
    }

    let isInTest = false;
    if (variant instanceof Array) {
        isInTest = variant.indexOf(testData.value) !== -1;
    } else if (variant >= 0) {
        isInTest = testData.value === variant;
    } else {
        isInTest = !!testData.value;
    }

    // This is kind of an experimental part.  We generally log from actions, but
    // we can, and perhaps should, log from here since shared components will.
    if (logEvaluation) {
        abacusLogExperiment(testData);
    }

    return isInTest;
}
/* eslint-enable complexity */

/**
 * This is the client-side 'easy' function to call.
 * It mimics what abacus-client-utils does, and REQUIRES window.__ABACUS__ to be defined.
 * @param {string} bucketName - The test name to evaluate.
 * @param {number|array} [variant=-1] - The target variant.  -1 (defaults) evaluates for all variants.
 * @param {boolean} [logEvaluation=false] - To execute the logging of a successful test.
 * @return {boolean}
 */
function isInBucket(bucketName, variant = -1, logEvaluation = false) {
    // eslint-disable-line
    if (typeof window === 'undefined' || !window.__ABACUS__) {
        // Abacus client utils tend to crash if __ABACUS__ is not defined.
        // We shouldn't know what's under the covers, though.
        return false;
    }

    return isInBucketData(window.__ABACUS__, bucketName, variant, logEvaluation);
}

/**
 * This is a doppelganger of the Experiment component, meant to mimic its interfact.
 * @param {object} abacus - The abucus test matrix; required only for ssr tests
 * @param {string} name - The experiment's name
 * @param {number|array} [variant=0] - The target variant.
 * @param {boolean} [deferLogging=false] - To not log the result. Default is to log.
 * @return {boolean}
 */
function evaluateExperiment({abacus = null, name = 'INVALID', variant = 0, deferLogging = false}) {
    const abacusData = abacus || (typeof window !== 'undefined' && window.__ABACUS__) || {};
    const inExperiment = evaluationIsVariant(abacusData[name], variant);

    if (!deferLogging) {
        abacusLogExperiment(abacusData[name]);
    }

    return inExperiment;
}

function logExperimentFromData(bucketData, bucketName) {
    const testData = bucketData[bucketName];

    if (!testData) {
        // This means the test is either not live, or something is wrong.
        return false;
    }

    return abacusLogExperiment(testData);
}

/**
 * This is the client-side 'easy' function to call.
 * It mimics what abacus-client-utils does, and REQUIRES window.__ABACUS__ to be difined.
 * @param {string} bucketName - The test name to evaluate.
 * @return {boolean}
 */
function logExperiment(bucketName) {
    if (typeof window === 'undefined' || !window.__ABACUS__) {
        // Abacus client utils tend to crash if __ABACUS__ is not defined.
        // We shouldn't know what's under the covers, though.
        return false;
    }

    return logExperimentFromData(window.__ABACUS__, bucketName);
}

/** Simple Function to get testData by evaluating an experiment which is active
 * @param {string} bucketName - The test name to evaluate.
 * @return {number, boolean}
 */
function getBucketValue(bucketName) {
    if (typeof window === 'undefined' || !window.__ABACUS__) {
        // Abacus client utils tend to crash if __ABACUS__ is not defined.
        // We shouldn't know what's under the covers, though.
        return false;
    }

    // evaluateExperiment just returns the bucket data from the global __ABACUS__
    const testData = abacusEvaluateExperiment(bucketName);

    if (!testData) {
        // This means the test is either not live, or something is wrong.
        return false;
    }

    return testData.value;
}

// The following functions are for getting abacus test bucket config data.
// PDP currently doesn't use this, but I wanted to get this in here.
// Because there aren't any tests using it, I made these function very generic.

/** Simple Function to get bucket config data.
 * @param {string} bucketName - The test name to evaluate.
 * @return {object, string, null}
 */
function getBucketConfigFromData(bucketData, bucketName) {
    const testData = bucketData[bucketName];

    if (!testData) {
        // This means the test is either not live, or something is wrong.
        return null;
    }

    const bucketConfig = (testData.bucket || {}).bucketConfig;
    let config = null;

    if (bucketConfig) {
        // If there is data, try to parse to see if it is an object.
        try {
            config = JSON.parse(bucketConfig);
        } catch (err) {
            // If the parse failed, then at lease return the value;
            config = bucketConfig;
        }
    }

    return config;
}

/** Simple Function to get bucket config data.
 * @param {string} bucketName - The test name to evaluate.
 * @return {object, string, null}
 */
function getBucketConfig(bucketName) {
    if (typeof window === 'undefined' || !window.__ABACUS__) {
        // Abacus client utils tend to crash if __ABACUS__ is not defined.
        // We shouldn't know what's under the covers, though.
        return null;
    }

    return getBucketConfigFromData(window.__ABACUS__, bucketName);
}

export default {
    evaluateExperiment,
    getBucketValue,
    isInBucket,
    isInBucketData,
    logExperiment,
    logExperimentFromData,
    getBucketConfig,
    getBucketConfigFromData
};

export {
    evaluateExperiment,
    getBucketValue,
    isInBucket,
    isInBucketData,
    logExperiment,
    logExperimentFromData,
    getBucketConfig,
    getBucketConfigFromData
};
